/**
 * Fortschritt von Key Results und Zielen.
 *
 * Die eine Stelle, an der man sich hier verrechnet: ein Key Result darf
 * ABWAERTS laufen. "Gewicht von 85 auf 78 kg" hat einen Zielwert kleiner als
 * den Startwert, und `current / target` liefert dafuer Unsinn. Die Formel
 * `(current - start) / (target - start)` funktioniert in beide Richtungen,
 * weil sich die Vorzeichen von Zaehler und Nenner aufheben.
 */
import { clamp01 } from '@/domain/goals/period';
import type { Goal, KeyResult } from '@/domain/types';

export type PaceStatus = 'voraus' | 'im_plan' | 'hinten';

/**
 * Toleranzband um den Zeitfortschritt, in dem ein Ziel als "im Plan" gilt.
 *
 * Ohne das waere fast jedes Ziel dauernd "hinten" - und eine Warnung, die
 * immer leuchtet, wird nicht mehr gelesen.
 */
export const PACE_TOLERANCE = 0.1;

/** Fortschritt eines Key Results, 0..1. */
export function keyResultProgress(kr: KeyResult): number {
  const span = kr.target - kr.start;
  // Start == Ziel: nichts zu erreichen, also entweder schon da oder nicht.
  if (span === 0) return kr.current === kr.target ? 1 : 0;
  return clamp01((kr.current - kr.start) / span);
}

/**
 * Fortschritt eines Ziels: ungewichtetes Mittel seiner Key Results.
 *
 * Ohne Key Results ist ein Ziel nicht messbar - dann ist der Fortschritt 0
 * und nicht etwa 100 %.
 */
export function goalProgress(goal: Goal): number {
  if (goal.keyResults.length === 0) return 0;
  const sum = goal.keyResults.reduce((acc, kr) => acc + keyResultProgress(kr), 0);
  return clamp01(sum / goal.keyResults.length);
}

/** Anteil 0..1 als ganze Prozent - die einzige Rundung der Anzeige. */
export function asPercent(anteil: number): number {
  return Math.round(anteil * 100);
}

/** Liegt der Fortschritt vor oder hinter der verstrichenen Zeit? */
export function paceStatus(progress: number, timeProgress: number): PaceStatus {
  const diff = progress - timeProgress;
  if (diff > PACE_TOLERANCE) return 'voraus';
  if (diff < -PACE_TOLERANCE) return 'hinten';
  return 'im_plan';
}

/**
 * Absolute Restmenge eines Key Results, z. B. "noch 8 kg".
 *
 * Ein uebertroffenes Ziel hat keinen Rest. Der Betrag allein reicht dafuer
 * nicht: bei "60 -> 75 kg, aktuell 90" waeren das sonst 15 kg Rest, obwohl
 * schon 15 kg zu viel geschafft sind.
 */
export function keyResultRemaining(kr: KeyResult): number {
  if (keyResultProgress(kr) >= 1) return 0;
  return Math.abs(kr.target - kr.current);
}
